import {
  KNOWN_DOMAIN_EVENT_TYPES,
  KNOWN_ENVELOPE_KINDS,
  PROTOCOL_VERSION,
  SEEDANCE_MODEL_IDS,
  type DomainEventPayload,
  type EnvelopeClassification,
  type JsonValue,
  type KnownDomainEventType,
  type KnownEnvelopeKind,
  type SessionEnvelope,
} from "./types.js";
import {
  CODEX_SCHEMA_CLIENT_REQUEST_METHODS,
  CODEX_SCHEMA_SERVER_NOTIFICATION_METHODS,
  CODEX_SCHEMA_SERVER_REQUEST_METHODS,
} from "./codex-app-server.js";

export interface ValidationIssue {
  path: string;
  code:
    | "type"
    | "required"
    | "unknown_key"
    | "format"
    | "range"
    | "conflict";
  message: string;
}

export type ValidationResult<T> =
  | { success: true; data: T; knownKind: boolean }
  | { success: false; issues: ValidationIssue[] };

export class ProtocolValidationError extends Error {
  readonly issues: ValidationIssue[];

  constructor(issues: ValidationIssue[]) {
    super(
      `Invalid ${PROTOCOL_VERSION} envelope: ${issues
        .map((issue) => `${issue.path} ${issue.message}`)
        .join("; ")}`,
    );
    this.name = "ProtocolValidationError";
    this.issues = issues;
  }
}

type UnknownRecord = Record<string, unknown>;
type IssueCollector = ValidationIssue[];

const knownEnvelopeKinds = new Set<string>(KNOWN_ENVELOPE_KINDS);
const knownDomainEventTypes = new Set<string>(KNOWN_DOMAIN_EVENT_TYPES);

const envelopeKeys = new Set([
  "protocol",
  "id",
  "kind",
  "sentAt",
  "cursor",
  "replyTo",
  "payload",
]);

const RFC3339 =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;
const SHA256 = /^[a-f0-9]{64}$/;
const MEDIA_TYPE =
  /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*(?:\s*;\s*[a-z0-9!#$&^_.+-]+=(?:[a-z0-9!#$&^_.+-]+|"[^"]*"))*$/i;
const BASE64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

function issue(
  issues: IssueCollector,
  path: string,
  code: ValidationIssue["code"],
  message: string,
): void {
  issues.push({ path, code, message });
}

function isPlainRecord(value: unknown): value is UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function expectRecord(
  value: unknown,
  path: string,
  issues: IssueCollector,
): UnknownRecord | null {
  if (!isPlainRecord(value)) {
    issue(issues, path, "type", "must be a plain object");
    return null;
  }
  return value;
}

function checkKeys(
  value: UnknownRecord,
  path: string,
  allowed: readonly string[],
  required: readonly string[],
  issues: IssueCollector,
): void {
  const allowedKeys = new Set(allowed);
  for (const key of Object.keys(value)) {
    if (!allowedKeys.has(key)) {
      issue(issues, `${path}.${key}`, "unknown_key", "is not allowed");
    }
  }
  for (const key of required) {
    if (!Object.hasOwn(value, key)) {
      issue(issues, `${path}.${key}`, "required", "is required");
    }
  }
}

function stringValue(
  value: unknown,
  path: string,
  issues: IssueCollector,
  options: { min?: number; max?: number; pattern?: RegExp } = {},
): value is string {
  if (typeof value !== "string") {
    issue(issues, path, "type", "must be a string");
    return false;
  }
  const min = options.min ?? 0;
  const max = options.max ?? 16_384;
  if (value.length < min || value.length > max) {
    issue(issues, path, "range", `must contain ${min}..${max} characters`);
    return false;
  }
  if (options.pattern && !options.pattern.test(value)) {
    issue(issues, path, "format", "has an invalid format");
    return false;
  }
  return true;
}

function enumValue<T extends string>(
  value: unknown,
  path: string,
  allowed: readonly T[],
  issues: IssueCollector,
): value is T {
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    issue(issues, path, "format", `must be one of: ${allowed.join(", ")}`);
    return false;
  }
  return true;
}

function booleanValue(
  value: unknown,
  path: string,
  issues: IssueCollector,
): value is boolean {
  if (typeof value !== "boolean") {
    issue(issues, path, "type", "must be a boolean");
    return false;
  }
  return true;
}

function integerValue(
  value: unknown,
  path: string,
  issues: IssueCollector,
  minimum = 0,
  maximum = Number.MAX_SAFE_INTEGER,
): value is number {
  if (
    typeof value !== "number" ||
    !Number.isSafeInteger(value) ||
    value < minimum ||
    value > maximum
  ) {
    issue(
      issues,
      path,
      "range",
      `must be a safe integer between ${minimum} and ${maximum}`,
    );
    return false;
  }
  return true;
}

function timestampValue(
  value: unknown,
  path: string,
  issues: IssueCollector,
): value is string {
  if (!stringValue(value, path, issues, { min: 20, max: 40 })) {
    return false;
  }
  if (!RFC3339.test(value) || Number.isNaN(Date.parse(value))) {
    issue(issues, path, "format", "must be an RFC 3339 timestamp");
    return false;
  }
  return true;
}

function optionalString(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: IssueCollector,
  options: { min?: number; max?: number; pattern?: RegExp } = {},
): void {
  if (Object.hasOwn(record, key)) {
    stringValue(record[key], `${path}.${key}`, issues, options);
  }
}

function optionalTimestamp(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: IssueCollector,
): void {
  if (Object.hasOwn(record, key)) {
    timestampValue(record[key], `${path}.${key}`, issues);
  }
}

function optionalInteger(
  record: UnknownRecord,
  key: string,
  path: string,
  issues: IssueCollector,
  minimum = 0,
): void {
  if (Object.hasOwn(record, key)) {
    integerValue(record[key], `${path}.${key}`, issues, minimum);
  }
}

/** Rejects non-JSON values, sparse arrays, non-finite numbers, and excessive nesting. */
export function isJsonValue(value: unknown): value is JsonValue {
  const budget = { remaining: 100_000 };

  function visit(candidate: unknown, depth: number): candidate is JsonValue {
    budget.remaining -= 1;
    if (budget.remaining < 0 || depth > 64) return false;
    if (
      candidate === null ||
      typeof candidate === "string" ||
      typeof candidate === "boolean"
    ) {
      return true;
    }
    if (typeof candidate === "number") return Number.isFinite(candidate);
    if (Array.isArray(candidate)) {
      for (let index = 0; index < candidate.length; index += 1) {
        if (!Object.hasOwn(candidate, index) || !visit(candidate[index], depth + 1)) {
          return false;
        }
      }
      return true;
    }
    if (!isPlainRecord(candidate)) return false;
    for (const nested of Object.values(candidate)) {
      if (!visit(nested, depth + 1)) return false;
    }
    return true;
  }

  return visit(value, 0);
}

function jsonValue(
  value: unknown,
  path: string,
  issues: IssueCollector,
): value is JsonValue {
  if (!isJsonValue(value)) {
    issue(issues, path, "type", "must be a finite, non-sparse JSON value");
    return false;
  }
  return true;
}

function stringArray(
  value: unknown,
  path: string,
  issues: IssueCollector,
  options: { minItems?: number; unique?: boolean } = {},
): value is string[] {
  if (!Array.isArray(value)) {
    issue(issues, path, "type", "must be an array");
    return false;
  }
  if (value.length < (options.minItems ?? 0)) {
    issue(issues, path, "range", "contains too few entries");
  }
  const seen = new Set<string>();
  value.forEach((entry, index) => {
    if (stringValue(entry, `${path}[${index}]`, issues, { min: 1, max: 256 })) {
      if (options.unique && seen.has(entry)) {
        issue(issues, `${path}[${index}]`, "conflict", "must be unique");
      }
      seen.add(entry);
    }
  });
  return issues.length === 0;
}

export function isKnownEnvelopeKind(value: string): value is KnownEnvelopeKind {
  return knownEnvelopeKinds.has(value);
}

export function isKnownDomainEventType(
  value: string,
): value is KnownDomainEventType {
  return knownDomainEventTypes.has(value);
}

function validateClientDescriptor(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["clientId", "name", "version", "platform"],
    ["clientId", "name", "version", "platform"],
    issues,
  );
  stringValue(record.clientId, `${path}.clientId`, issues, { min: 1, max: 128 });
  stringValue(record.name, `${path}.name`, issues, { min: 1, max: 128 });
  stringValue(record.version, `${path}.version`, issues, { min: 1, max: 64 });
  enumValue(record.platform, `${path}.platform`, ["web", "windows", "android"], issues);
}

function validateConnectionHello(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["client", "supportedProtocols", "lastSessionId"],
    ["client", "supportedProtocols"],
    issues,
  );
  validateClientDescriptor(record.client, `${path}.client`, issues);
  if (Array.isArray(record.supportedProtocols)) {
    if (record.supportedProtocols.length === 0) {
      issue(issues, `${path}.supportedProtocols`, "range", "must not be empty");
    }
    record.supportedProtocols.forEach((protocol, index) => {
      if (protocol !== PROTOCOL_VERSION) {
        issue(
          issues,
          `${path}.supportedProtocols[${index}]`,
          "format",
          `must equal ${PROTOCOL_VERSION}`,
        );
      }
    });
  } else {
    issue(issues, `${path}.supportedProtocols`, "type", "must be an array");
  }
  optionalString(record, "lastSessionId", path, issues, { min: 1, max: 128 });
}

function validateConnectionWelcome(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    [
      "connectionId",
      "sessionId",
      "principalId",
      "selectedProtocol",
      "serverTime",
      "heartbeatIntervalMs",
      "maxMessageBytes",
      "resumeSupported",
    ],
    [
      "connectionId",
      "sessionId",
      "selectedProtocol",
      "serverTime",
      "heartbeatIntervalMs",
      "maxMessageBytes",
      "resumeSupported",
    ],
    issues,
  );
  stringValue(record.connectionId, `${path}.connectionId`, issues, {
    min: 1,
    max: 128,
  });
  stringValue(record.sessionId, `${path}.sessionId`, issues, { min: 1, max: 128 });
  optionalString(record, "principalId", path, issues, { min: 16, max: 128 });
  if (record.selectedProtocol !== PROTOCOL_VERSION) {
    issue(
      issues,
      `${path}.selectedProtocol`,
      "format",
      `must equal ${PROTOCOL_VERSION}`,
    );
  }
  timestampValue(record.serverTime, `${path}.serverTime`, issues);
  integerValue(
    record.heartbeatIntervalMs,
    `${path}.heartbeatIntervalMs`,
    issues,
    1_000,
    300_000,
  );
  integerValue(
    record.maxMessageBytes,
    `${path}.maxMessageBytes`,
    issues,
    1_024,
    1_073_741_824,
  );
  booleanValue(record.resumeSupported, `${path}.resumeSupported`, issues);
}

function validateHeartbeat(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["nonce", "direction", "lastReceivedCursor"],
    ["nonce", "direction"],
    issues,
  );
  stringValue(record.nonce, `${path}.nonce`, issues, { min: 1, max: 128 });
  enumValue(record.direction, `${path}.direction`, ["ping", "pong"], issues);
  optionalInteger(record, "lastReceivedCursor", path, issues);
}

function validateResume(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["sessionId", "clientId", "afterCursor", "previousConnectionId"],
    ["sessionId", "clientId"],
    issues,
  );
  stringValue(record.sessionId, `${path}.sessionId`, issues, { min: 1, max: 128 });
  stringValue(record.clientId, `${path}.clientId`, issues, { min: 1, max: 128 });
  optionalInteger(record, "afterCursor", path, issues);
  optionalString(record, "previousConnectionId", path, issues, {
    min: 1,
    max: 128,
  });
}

function validateResumed(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["sessionId", "resetRequired", "replayedThroughCursor", "reason"],
    ["sessionId", "resetRequired"],
    issues,
  );
  stringValue(record.sessionId, `${path}.sessionId`, issues, { min: 1, max: 128 });
  booleanValue(record.resetRequired, `${path}.resetRequired`, issues);
  optionalInteger(record, "replayedThroughCursor", path, issues);
  optionalString(record, "reason", path, issues, { min: 1, max: 1_024 });
}

function validateRpcRequest(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(record, path, ["method", "params"], ["method"], issues);
  stringValue(record.method, `${path}.method`, issues, {
    min: 1,
    max: 256,
    pattern: /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
  });
  if (Object.hasOwn(record, "params")) {
    jsonValue(record.params, `${path}.params`, issues);
  }
}

function validateRpcError(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["code", "message", "retryable", "details"],
    ["code", "message", "retryable"],
    issues,
  );
  stringValue(record.code, `${path}.code`, issues, {
    min: 1,
    max: 128,
    pattern: /^[A-Z0-9][A-Z0-9_.-]*$/i,
  });
  stringValue(record.message, `${path}.message`, issues, { min: 1, max: 8_192 });
  booleanValue(record.retryable, `${path}.retryable`, issues);
  if (Object.hasOwn(record, "details")) {
    jsonValue(record.details, `${path}.details`, issues);
  }
}

function validateRpcResponse(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(record, path, ["result", "error"], [], issues);
  const hasResult = Object.hasOwn(record, "result");
  const hasError = Object.hasOwn(record, "error");
  if (hasResult === hasError) {
    issue(
      issues,
      path,
      "conflict",
      "must contain exactly one of result or error",
    );
  }
  if (hasResult) jsonValue(record.result, `${path}.result`, issues);
  if (hasError) validateRpcError(record.error, `${path}.error`, issues);
}

function validateKnownDomainData(
  eventType: KnownDomainEventType,
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;

  switch (eventType) {
    case "session.ready":
      checkKeys(record, path, ["sessionId"], ["sessionId"], issues);
      stringValue(record.sessionId, `${path}.sessionId`, issues, {
        min: 1,
        max: 128,
      });
      break;
    case "session.state":
      checkKeys(record, path, ["state", "reason"], ["state"], issues);
      enumValue(
        record.state,
        `${path}.state`,
        ["idle", "running", "waiting_for_user", "offline", "error"],
        issues,
      );
      optionalString(record, "reason", path, issues, { min: 1, max: 1_024 });
      break;
    case "turn.started":
      checkKeys(record, path, ["turnId", "threadId"], ["turnId"], issues);
      stringValue(record.turnId, `${path}.turnId`, issues, { min: 1, max: 128 });
      optionalString(record, "threadId", path, issues, { min: 1, max: 128 });
      break;
    case "turn.progress": {
      checkKeys(
        record,
        path,
        ["turnId", "phase", "label", "completed", "total"],
        ["turnId", "phase"],
        issues,
      );
      stringValue(record.turnId, `${path}.turnId`, issues, { min: 1, max: 128 });
      stringValue(record.phase, `${path}.phase`, issues, { min: 1, max: 128 });
      optionalString(record, "label", path, issues, { min: 1, max: 1_024 });
      optionalInteger(record, "completed", path, issues);
      optionalInteger(record, "total", path, issues, 1);
      if (
        typeof record.completed === "number" &&
        typeof record.total === "number" &&
        record.completed > record.total
      ) {
        issue(issues, `${path}.completed`, "range", "must not exceed total");
      }
      break;
    }
    case "turn.completed":
      checkKeys(record, path, ["turnId", "status", "error"], ["turnId", "status"], issues);
      stringValue(record.turnId, `${path}.turnId`, issues, { min: 1, max: 128 });
      enumValue(
        record.status,
        `${path}.status`,
        ["completed", "cancelled", "interrupted", "failed"],
        issues,
      );
      if (Object.hasOwn(record, "error")) {
        validateRpcError(record.error, `${path}.error`, issues);
      }
      break;
    case "message.started":
      checkKeys(record, path, ["messageId", "role"], ["messageId", "role"], issues);
      stringValue(record.messageId, `${path}.messageId`, issues, {
        min: 1,
        max: 128,
      });
      enumValue(record.role, `${path}.role`, ["user", "assistant", "tool"], issues);
      break;
    case "message.delta":
      checkKeys(record, path, ["messageId", "delta"], ["messageId", "delta"], issues);
      stringValue(record.messageId, `${path}.messageId`, issues, {
        min: 1,
        max: 128,
      });
      stringValue(record.delta, `${path}.delta`, issues, { max: 1_048_576 });
      break;
    case "message.completed":
      checkKeys(record, path, ["messageId"], ["messageId"], issues);
      stringValue(record.messageId, `${path}.messageId`, issues, {
        min: 1,
        max: 128,
      });
      break;
    case "approval.requested":
      checkKeys(
        record,
        path,
        ["requestId", "method", "title"],
        ["requestId", "method"],
        issues,
      );
      stringValue(record.requestId, `${path}.requestId`, issues, {
        min: 1,
        max: 128,
      });
      stringValue(record.method, `${path}.method`, issues, { min: 1, max: 256 });
      optionalString(record, "title", path, issues, { min: 1, max: 512 });
      break;
    case "approval.resolved":
      checkKeys(record, path, ["requestId", "outcome"], ["requestId", "outcome"], issues);
      stringValue(record.requestId, `${path}.requestId`, issues, {
        min: 1,
        max: 128,
      });
      enumValue(
        record.outcome,
        `${path}.outcome`,
        ["accepted", "declined", "cancelled", "expired", "error"],
        issues,
      );
      break;
    case "artifact.available":
      checkKeys(record, path, ["artifactId"], ["artifactId"], issues);
      stringValue(record.artifactId, `${path}.artifactId`, issues, {
        min: 1,
        max: 128,
      });
      break;
    case "video.task.created":
      checkKeys(
        record,
        path,
        ["taskId", "requestId", "provider", "model", "status"],
        ["taskId", "requestId", "provider", "model", "status"],
        issues,
      );
      stringValue(record.taskId, `${path}.taskId`, issues, { min: 1, max: 128 });
      stringValue(record.requestId, `${path}.requestId`, issues, { min: 1, max: 128 });
      enumValue(record.provider, `${path}.provider`, ["volcengine_ark"], issues);
      enumValue(record.model, `${path}.model`, SEEDANCE_MODEL_IDS, issues);
      enumValue(record.status, `${path}.status`, ["queued"], issues);
      break;
    case "video.task.updated":
      checkKeys(record, path, ["taskId", "status", "label"], ["taskId", "status"], issues);
      stringValue(record.taskId, `${path}.taskId`, issues, { min: 1, max: 128 });
      enumValue(record.status, `${path}.status`, ["queued", "running", "expired"], issues);
      optionalString(record, "label", path, issues, { min: 1, max: 1_024 });
      break;
    case "video.task.completed":
      checkKeys(record, path, ["taskId", "status", "artifactId"], ["taskId", "status", "artifactId"], issues);
      stringValue(record.taskId, `${path}.taskId`, issues, { min: 1, max: 128 });
      enumValue(record.status, `${path}.status`, ["succeeded"], issues);
      stringValue(record.artifactId, `${path}.artifactId`, issues, { min: 1, max: 128 });
      break;
    case "video.task.cancelled":
      checkKeys(record, path, ["taskId", "status"], ["taskId", "status"], issues);
      stringValue(record.taskId, `${path}.taskId`, issues, { min: 1, max: 128 });
      enumValue(record.status, `${path}.status`, ["cancelled"], issues);
      break;
    case "video.task.failed":
      checkKeys(record, path, ["taskId", "status", "error"], ["taskId", "status", "error"], issues);
      stringValue(record.taskId, `${path}.taskId`, issues, { min: 1, max: 128 });
      enumValue(record.status, `${path}.status`, ["failed", "expired"], issues);
      validateRpcError(record.error, `${path}.error`, issues);
      break;
    case "capabilities.changed":
      checkKeys(record, path, ["snapshotId"], ["snapshotId"], issues);
      stringValue(record.snapshotId, `${path}.snapshotId`, issues, {
        min: 1,
        max: 128,
      });
      break;
    case "error":
      validateRpcError(record, path, issues);
      break;
  }
}

function validateDomainEvent(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["eventId", "type", "occurredAt", "data"],
    ["eventId", "type", "occurredAt", "data"],
    issues,
  );
  stringValue(record.eventId, `${path}.eventId`, issues, { min: 1, max: 128 });
  const eventType = record.type;
  const validType = stringValue(eventType, `${path}.type`, issues, {
    min: 1,
    max: 256,
    pattern: /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
  });
  timestampValue(record.occurredAt, `${path}.occurredAt`, issues);
  if (validType && isKnownDomainEventType(eventType)) {
    validateKnownDomainData(eventType, record.data, `${path}.data`, issues);
  } else {
    // Unknown domain events stay byte-for-byte JSON-equivalent and are forwarded.
    jsonValue(record.data, `${path}.data`, issues);
  }
}

function validateElicitationRequest(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["requestId", "source", "method", "category", "params", "title", "expiresAt"],
    ["requestId", "source", "method", "category", "params"],
    issues,
  );
  stringValue(record.requestId, `${path}.requestId`, issues, { min: 1, max: 128 });
  enumValue(record.source, `${path}.source`, ["codex_app_server", "gateway"], issues);
  stringValue(record.method, `${path}.method`, issues, {
    min: 1,
    max: 256,
    pattern: /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
  });
  enumValue(
    record.category,
    `${path}.category`,
    [
      "approval",
      "user_input",
      "mcp_elicitation",
      "tool_call",
      "auth_refresh",
      "attestation",
    ],
    issues,
  );
  if (typeof record.method === "string" && typeof record.category === "string") {
    const categories: Record<string, string> = {
      "item/commandExecution/requestApproval": "approval",
      "item/fileChange/requestApproval": "approval",
      "item/permissions/requestApproval": "approval",
      "applyPatchApproval": "approval",
      "execCommandApproval": "approval",
      "mcpServer/elicitation/request": "mcp_elicitation",
      "item/tool/call": "tool_call",
      "item/tool/requestUserInput": "user_input",
      "account/chatgptAuthTokens/refresh": "auth_refresh",
      "attestation/generate": "attestation",
    };
    const expected = categories[record.method];
    if (expected && record.category !== expected) {
      issue(issues, `${path}.category`, "conflict", `must be ${expected} for ${record.method}`);
    }
  }
  jsonValue(record.params, `${path}.params`, issues);
  optionalString(record, "title", path, issues, { min: 1, max: 512 });
  optionalTimestamp(record, "expiresAt", path, issues);
}

function validateElicitationResponse(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(record, path, ["outcome", "result", "error"], ["outcome"], issues);
  const outcomeValid = enumValue(
    record.outcome,
    `${path}.outcome`,
    ["accepted", "declined", "cancelled", "expired", "error"],
    issues,
  );
  if (Object.hasOwn(record, "result")) {
    jsonValue(record.result, `${path}.result`, issues);
  }
  if (Object.hasOwn(record, "error")) {
    validateRpcError(record.error, `${path}.error`, issues);
  }
  if (outcomeValid) {
    if (record.outcome === "error" && !Object.hasOwn(record, "error")) {
      issue(issues, `${path}.error`, "required", "is required for an error outcome");
    }
    if (record.outcome !== "error" && Object.hasOwn(record, "error")) {
      issue(issues, `${path}.error`, "conflict", "is only valid for an error outcome");
    }
  }
}

function validateRetention(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["serverStored", "objectKey", "retainedUntil"],
    ["serverStored"],
    issues,
  );
  booleanValue(record.serverStored, `${path}.serverStored`, issues);
  optionalString(record, "objectKey", path, issues, { min: 1, max: 1_024 });
  optionalTimestamp(record, "retainedUntil", path, issues);
  if (record.serverStored === false) {
    if (Object.hasOwn(record, "objectKey")) {
      issue(issues, `${path}.objectKey`, "conflict", "requires serverStored=true");
    }
    if (Object.hasOwn(record, "retainedUntil")) {
      issue(issues, `${path}.retainedUntil`, "conflict", "requires serverStored=true");
    }
  }
}

function validateTransfer(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  const modeValid = enumValue(record.mode, `${path}.mode`, ["chunks", "url"], issues);
  if (!modeValid) return;
  if (record.mode === "chunks") {
    checkKeys(record, path, ["mode", "chunkSize", "chunkCount"], ["mode", "chunkSize", "chunkCount"], issues);
    integerValue(record.chunkSize, `${path}.chunkSize`, issues, 1, 16_777_216);
    integerValue(record.chunkCount, `${path}.chunkCount`, issues, 1, 10_000_000);
  } else {
    checkKeys(record, path, ["mode"], ["mode"], issues);
  }
}

function isSafeRelativePath(value: string): boolean {
  if (
    value.length === 0 ||
    value.length > 1_024 ||
    value.startsWith("/") ||
    value.startsWith("\\") ||
    value.includes("\\") ||
    value.includes("\0") ||
    /^[A-Za-z]:/.test(value)
  ) {
    return false;
  }
  const segments = value.split("/");
  return segments.every(
    (segment) =>
      segment.length > 0 &&
      segment !== "." &&
      segment !== ".." &&
      !/[<>:"|?*]/.test(segment) &&
      !/[. ]$/.test(segment),
  );
}

function validateManifest(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    [
      "artifactId",
      "projectId",
      "fileName",
      "relativePath",
      "mediaType",
      "size",
      "sha256",
      "transfer",
      "retention",
      "createdAt",
    ],
    [
      "artifactId",
      "projectId",
      "fileName",
      "relativePath",
      "mediaType",
      "size",
      "sha256",
      "transfer",
      "retention",
      "createdAt",
    ],
    issues,
  );
  stringValue(record.artifactId, `${path}.artifactId`, issues, { min: 1, max: 128 });
  stringValue(record.projectId, `${path}.projectId`, issues, { min: 1, max: 128 });
  if (stringValue(record.fileName, `${path}.fileName`, issues, { min: 1, max: 255 })) {
    if (!isSafeRelativePath(record.fileName) || record.fileName.includes("/")) {
      issue(issues, `${path}.fileName`, "format", "must be a safe file name");
    }
  }
  if (
    stringValue(record.relativePath, `${path}.relativePath`, issues, {
      min: 1,
      max: 1_024,
    }) &&
    !isSafeRelativePath(record.relativePath)
  ) {
    issue(issues, `${path}.relativePath`, "format", "must be a safe relative path");
  }
  stringValue(record.mediaType, `${path}.mediaType`, issues, {
    min: 3,
    max: 255,
    pattern: MEDIA_TYPE,
  });
  integerValue(record.size, `${path}.size`, issues, 0);
  stringValue(record.sha256, `${path}.sha256`, issues, { pattern: SHA256 });
  validateTransfer(record.transfer, `${path}.transfer`, issues);
  validateRetention(record.retention, `${path}.retention`, issues);
  timestampValue(record.createdAt, `${path}.createdAt`, issues);
}

function validateArtifactChunk(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["artifactId", "index", "offset", "dataBase64", "chunkSha256", "final"],
    ["artifactId", "index", "offset", "dataBase64", "chunkSha256", "final"],
    issues,
  );
  stringValue(record.artifactId, `${path}.artifactId`, issues, { min: 1, max: 128 });
  integerValue(record.index, `${path}.index`, issues);
  integerValue(record.offset, `${path}.offset`, issues);
  stringValue(record.dataBase64, `${path}.dataBase64`, issues, {
    max: 22_369_624,
    pattern: BASE64,
  });
  stringValue(record.chunkSha256, `${path}.chunkSha256`, issues, {
    pattern: SHA256,
  });
  booleanValue(record.final, `${path}.final`, issues);
}

function validateArtifactUrl(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["artifactId", "url", "expiresAt", "sha256"],
    ["artifactId", "url", "expiresAt", "sha256"],
    issues,
  );
  stringValue(record.artifactId, `${path}.artifactId`, issues, { min: 1, max: 128 });
  if (stringValue(record.url, `${path}.url`, issues, { min: 1, max: 8_192 })) {
    try {
      const url = new URL(record.url);
      const isLocal = url.hostname === "localhost" || url.hostname === "127.0.0.1";
      if (url.protocol !== "https:" && !(isLocal && url.protocol === "http:")) {
        issue(
          issues,
          `${path}.url`,
          "format",
          "must use HTTPS (HTTP is allowed only for localhost)",
        );
      }
      if (url.username || url.password) {
        issue(issues, `${path}.url`, "format", "must not contain URL credentials");
      }
    } catch {
      issue(issues, `${path}.url`, "format", "must be an absolute URL");
    }
  }
  timestampValue(record.expiresAt, `${path}.expiresAt`, issues);
  stringValue(record.sha256, `${path}.sha256`, issues, { pattern: SHA256 });
}

const capabilityStatuses = [
  "available",
  "unavailable",
  "auth_required",
  "degraded",
  "experimental",
] as const;
const capabilitySources = [
  "codex_app_server",
  "openai_api",
  "provider_api",
  "gateway_extension",
  "none",
] as const;

function validateModeCapability(
  value: unknown,
  path: string,
  expectedMode: "chat" | "work" | "video",
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(record, path, ["mode", "status", "source", "reason"], ["mode", "status", "source"], issues);
  if (record.mode !== expectedMode) {
    issue(issues, `${path}.mode`, "format", `must equal ${expectedMode}`);
  }
  enumValue(record.status, `${path}.status`, capabilityStatuses, issues);
  enumValue(record.source, `${path}.source`, capabilitySources, issues);
  optionalString(record, "reason", path, issues, { min: 1, max: 2_048 });

  if ((expectedMode === "chat" || expectedMode === "video") && record.source === "codex_app_server") {
    issue(
      issues,
      `${path}.source`,
      "conflict",
      `Codex app-server is a work-agent interface, not a ${expectedMode} provider`,
    );
  }
  if (record.status === "unavailable" && record.source !== "none") {
    issue(issues, `${path}.source`, "conflict", "must be none when unavailable");
  }
  if (record.status !== "unavailable" && record.source === "none") {
    issue(issues, `${path}.source`, "conflict", "must identify an implementation source");
  }
}

function validateFeatureCapability(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["id", "status", "source", "operations", "reason"],
    ["id", "status", "source", "operations"],
    issues,
  );
  stringValue(record.id, `${path}.id`, issues, {
    min: 1,
    max: 256,
    pattern: /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
  });
  enumValue(record.status, `${path}.status`, capabilityStatuses, issues);
  enumValue(record.source, `${path}.source`, capabilitySources, issues);
  stringArray(record.operations, `${path}.operations`, issues, { unique: true });
  optionalString(record, "reason", path, issues, { min: 1, max: 2_048 });
  if (record.status === "unavailable" && record.source !== "none") {
    issue(issues, `${path}.source`, "conflict", "must be none when unavailable");
  }
  if (record.status !== "unavailable" && record.source === "none") {
    issue(issues, `${path}.source`, "conflict", "must identify an implementation source");
  }
}

function validateCodexCapability(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    [
      "codexCliVersion",
      "schemaGeneratedWithoutExperimental",
      "clientRequests",
      "serverNotifications",
      "reverseRequests",
    ],
    [
      "codexCliVersion",
      "schemaGeneratedWithoutExperimental",
      "clientRequests",
      "serverNotifications",
      "reverseRequests",
    ],
    issues,
  );
  stringValue(record.codexCliVersion, `${path}.codexCliVersion`, issues, {
    min: 1,
    max: 64,
  });
  booleanValue(
    record.schemaGeneratedWithoutExperimental,
    `${path}.schemaGeneratedWithoutExperimental`,
    issues,
  );
  stringArray(record.clientRequests, `${path}.clientRequests`, issues, {
    unique: true,
  });
  stringArray(record.serverNotifications, `${path}.serverNotifications`, issues, {
    unique: true,
  });
  stringArray(record.reverseRequests, `${path}.reverseRequests`, issues, {
    unique: true,
  });

  if (record.schemaGeneratedWithoutExperimental === true) {
    const allowedClient = new Set<string>(CODEX_SCHEMA_CLIENT_REQUEST_METHODS);
    const allowedNotifications = new Set<string>(
      CODEX_SCHEMA_SERVER_NOTIFICATION_METHODS,
    );
    const allowedReverse = new Set<string>(CODEX_SCHEMA_SERVER_REQUEST_METHODS);
    const checkAllowList = (
      entries: unknown,
      allowed: Set<string>,
      childPath: string,
    ): void => {
      if (!Array.isArray(entries)) return;
      entries.forEach((entry, index) => {
        if (typeof entry === "string" && !allowed.has(entry)) {
          issue(
            issues,
            `${childPath}[${index}]`,
            "format",
            "is not in the non-experimental schema snapshot",
          );
        }
      });
    };
    checkAllowList(record.clientRequests, allowedClient, `${path}.clientRequests`);
    checkAllowList(
      record.serverNotifications,
      allowedNotifications,
      `${path}.serverNotifications`,
    );
    checkAllowList(record.reverseRequests, allowedReverse, `${path}.reverseRequests`);
  }
}

function validateCapabilities(
  value: unknown,
  path: string,
  issues: IssueCollector,
): void {
  const record = expectRecord(value, path, issues);
  if (!record) return;
  checkKeys(
    record,
    path,
    ["snapshotId", "generatedAt", "modes", "features", "codexAppServer"],
    ["snapshotId", "generatedAt", "modes", "features"],
    issues,
  );
  stringValue(record.snapshotId, `${path}.snapshotId`, issues, { min: 1, max: 128 });
  timestampValue(record.generatedAt, `${path}.generatedAt`, issues);

  const modes = expectRecord(record.modes, `${path}.modes`, issues);
  if (modes) {
    checkKeys(modes, `${path}.modes`, ["chat", "work", "video"], ["chat", "work"], issues);
    validateModeCapability(modes.chat, `${path}.modes.chat`, "chat", issues);
    validateModeCapability(modes.work, `${path}.modes.work`, "work", issues);
    if (Object.hasOwn(modes, "video")) {
      validateModeCapability(modes.video, `${path}.modes.video`, "video", issues);
    }
  }

  if (!Array.isArray(record.features)) {
    issue(issues, `${path}.features`, "type", "must be an array");
  } else {
    const featureIds = new Set<string>();
    record.features.forEach((feature, index) => {
      const featurePath = `${path}.features[${index}]`;
      validateFeatureCapability(feature, featurePath, issues);
      if (isPlainRecord(feature) && typeof feature.id === "string") {
        if (featureIds.has(feature.id)) {
          issue(issues, `${featurePath}.id`, "conflict", "must be unique");
        }
        featureIds.add(feature.id);
      }
    });
  }
  if (Object.hasOwn(record, "codexAppServer")) {
    validateCodexCapability(record.codexAppServer, `${path}.codexAppServer`, issues);
  }
}

const payloadValidators: Record<
  KnownEnvelopeKind,
  (value: unknown, path: string, issues: IssueCollector) => void
> = {
  "connection.hello": validateConnectionHello,
  "connection.welcome": validateConnectionWelcome,
  "connection.heartbeat": validateHeartbeat,
  "connection.resume": validateResume,
  "connection.resumed": validateResumed,
  "rpc.request": validateRpcRequest,
  "rpc.response": validateRpcResponse,
  "domain.event": validateDomainEvent,
  "elicitation.request": validateElicitationRequest,
  "elicitation.response": validateElicitationResponse,
  "artifact.manifest": validateManifest,
  "artifact.chunk": validateArtifactChunk,
  "artifact.url": validateArtifactUrl,
  "capabilities.snapshot": validateCapabilities,
};

export function safeParseSessionEnvelope(
  input: unknown,
): ValidationResult<SessionEnvelope> {
  const issues: IssueCollector = [];
  const record = expectRecord(input, "$", issues);
  if (!record) return { success: false, issues };

  for (const key of Object.keys(record)) {
    if (!envelopeKeys.has(key)) {
      issue(issues, `$.${key}`, "unknown_key", "is not allowed");
    }
  }
  for (const key of ["protocol", "id", "kind", "sentAt", "payload"]) {
    if (!Object.hasOwn(record, key)) {
      issue(issues, `$.${key}`, "required", "is required");
    }
  }

  if (record.protocol !== PROTOCOL_VERSION) {
    issue(issues, "$.protocol", "format", `must equal ${PROTOCOL_VERSION}`);
  }
  stringValue(record.id, "$.id", issues, { min: 1, max: 128 });
  const validKind = stringValue(record.kind, "$.kind", issues, {
    min: 1,
    max: 256,
    pattern: /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
  });
  const kind = validKind && typeof record.kind === "string" ? record.kind : "";
  timestampValue(record.sentAt, "$.sentAt", issues);
  optionalInteger(record, "cursor", "$", issues, kind === "elicitation.request" ? 1 : 0);
  optionalString(record, "replyTo", "$", issues, { min: 1, max: 128 });
  const validJsonPayload = jsonValue(record.payload, "$.payload", issues);

  const knownKind = validKind && isKnownEnvelopeKind(kind);
  if (knownKind && validJsonPayload) {
    payloadValidators[kind](record.payload, "$.payload", issues);
  }

  if (
    (kind === "rpc.response" || kind === "elicitation.response") &&
    !Object.hasOwn(record, "replyTo")
  ) {
    issue(issues, "$.replyTo", "required", `is required for ${kind}`);
  }
  if (
    (kind === "rpc.request" || kind === "elicitation.request") &&
    Object.hasOwn(record, "replyTo")
  ) {
    issue(issues, "$.replyTo", "conflict", `is not valid for ${kind}`);
  }
  if (kind === "elicitation.request") {
    if (!Object.hasOwn(record, "cursor")) {
      issue(issues, "$.cursor", "required", "is required for elicitation.request");
    }
    const elicitation = record.payload;
    if (
      elicitation &&
      typeof elicitation === "object" &&
      !Array.isArray(elicitation) &&
      typeof (elicitation as UnknownRecord).requestId === "string" &&
      typeof record.id === "string" &&
      (elicitation as UnknownRecord).requestId !== record.id
    ) {
      issue(issues, "$.payload.requestId", "conflict", "must equal the envelope id");
    }
  }

  if (issues.length > 0) return { success: false, issues };
  return {
    success: true,
    data: input as SessionEnvelope,
    knownKind,
  };
}

/**
 * Strict type guard for wire data. Known kinds receive their full payload
 * validation; unknown kinds are accepted only when the base envelope and JSON
 * payload are valid, allowing a newer gateway to pass through new messages.
 */
export function isSessionEnvelope(input: unknown): input is SessionEnvelope {
  return safeParseSessionEnvelope(input).success;
}

export function parseSessionEnvelope(input: unknown): SessionEnvelope {
  const result = safeParseSessionEnvelope(input);
  if (!result.success) throw new ProtocolValidationError(result.issues);
  return result.data;
}

export function classifySessionEnvelope(
  input: unknown,
): EnvelopeClassification {
  const envelope = parseSessionEnvelope(input);
  const knownKind = isKnownEnvelopeKind(envelope.kind);
  let knownDomainEvent: boolean | null = null;
  if (envelope.kind === "domain.event") {
    const event = envelope.payload as unknown as DomainEventPayload;
    knownDomainEvent = isKnownDomainEventType(event.type);
  }
  return { envelope, knownKind, knownDomainEvent };
}

export function decodeSessionEnvelope(json: string): SessionEnvelope {
  let value: unknown;
  try {
    value = JSON.parse(json) as unknown;
  } catch {
    throw new ProtocolValidationError([
      { path: "$", code: "format", message: "must be valid JSON" },
    ]);
  }
  return parseSessionEnvelope(value);
}

export function encodeSessionEnvelope(envelope: SessionEnvelope): string {
  return JSON.stringify(parseSessionEnvelope(envelope));
}

export function createSessionEnvelope<
  TKind extends string,
  TPayload extends JsonValue,
>(input: {
  id: string;
  kind: TKind;
  sentAt?: string;
  cursor?: number;
  replyTo?: string;
  payload: TPayload;
}): SessionEnvelope<TKind, TPayload> {
  const envelope: SessionEnvelope<TKind, TPayload> = {
    protocol: PROTOCOL_VERSION,
    id: input.id,
    kind: input.kind,
    sentAt: input.sentAt ?? new Date().toISOString(),
    payload: input.payload,
    ...(input.cursor === undefined ? {} : { cursor: input.cursor }),
    ...(input.replyTo === undefined ? {} : { replyTo: input.replyTo }),
  };
  return parseSessionEnvelope(envelope) as SessionEnvelope<TKind, TPayload>;
}
